'use client';

import Image from "next/image";
import Link from "next/link";
import Button from "../ui/Button";

export default function HomeHeroSection() {
  return (
    <section className="relative overflow-hidden sm:mt-6 mt-4">
      <div className="mx-auto w-full px-4 sm:px-8">
        <div className="w-full min-h-[540px] lg:min-h-[650px] bg-blue-100 sm:rounded-2xl rounded-2xl overflow-hidden relative">
          <div className="absolute top-0 left-0 w-full h-full">
            <Image
              src="/media/pages/home/hero-background.webp"
              alt="Dank Cannabis - Premium cannabis products background"
              fill
              priority
              fetchPriority="high"
              quality={60}
              className="object-cover"
              sizes="(max-width: 640px) 100vw, (max-width: 1280px) 92vw, 1280px"
            />
          </div>

          <div className="relative lg:absolute bottom-0 left-0 w-full lg:w-1/2 px-4 lg:px-8 py-4 lg:pb-12">
            <div className="flex flex-col items-start gap-6 lg:gap-8">
              <div className="flex flex-col items-start gap-3">
                <h1 className="text-h1-mobile lg:text-h1-desktop font-bold text-constant-white leading-tight">
                  Your next favorite strain is here
                </h1>
                <p className="max-sm:hidden text-body-lg font-medium text-constant-white opacity-80 leading-relaxed max-w-xl">
                  Hit the Dank catalog and explore our full weed lineup – from classic strains to fresh drops and others
                </p>
                <p className="sm:hidden text-body-14 font-normal text-constant-white opacity-80 leading-relaxed max-w-xl">
                  Browse the Dank catalog to explore our full weed lineup, from classic strains to fresh drops.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 max-sm:w-full">
                <Button asChild variant="bordered" color="white" size="lg" className="rounded-xl px-8 py-3 max-sm:w-full hover:border-white">
                  <Link href="/search">Explore all products</Link>
                </Button>
                <Button asChild variant="glass" size="lg" className="rounded-xl px-8 py-3 max-sm:w-full">
                  <Link href="/search?deals=true">Shop deals</Link>
                </Button>
              </div>
            </div>
          </div>

          <div className="hidden lg:block absolute top-4 right-4 w-[calc(50%-1rem)] max-w-[648px]">
            <div className="relative w-full aspect-[648/305] bg-constant-white rounded-2xl overflow-hidden shadow-lg transition-shadow mb-3">
              <div
                className="absolute inset-0 bg-cover bg-center bg-blue-5"
                style={{
                  backgroundImage: "url(/media/pages/home/cta/pickup-background.webp)",
                }}
              ></div>

              <Image
                fill
                src="/media/pages/home/cta/pickup.webp"
                alt="Cannabis products available for in-store pickup at Dank Cannabis"
                className="object-cover"
                sizes="(max-width: 1024px) 100vw, 50vw"
                loading="lazy"
                decoding="async"
              />

              <div className="relative z-10 flex flex-col justify-between h-full p-4">
                <h2 className="max-w-[420px] text-h4-mobile xl:text-h4-desktop font-bold text-blue-100 leading-tight">
                  Pick up in store from one of the best menus in the game.
                </h2>
                <Button asChild variant="bordered" color="white" size="lg" className="rounded-xl px-4 py-3 sm:w-max w-full">
                  <Link href="/search?fulfillment=pickup">Pick up today</Link>
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="relative aspect-[320/305] bg-blue-5 rounded-2xl overflow-hidden border-2 border-blue-100 transition-shadow">
                <div
                  className="absolute inset-0 bg-cover bg-center bg-blue-5"
                  style={{
                    backgroundImage: "url(/media/pages/home/cta/delivery-background.webp)",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                ></div>

                <div className="relative z-10 flex flex-col justify-between h-full p-5">
                  <h2 className="text-body-18 xl:text-h4-desktop font-bold text-blue-100 leading-tight">
                    Dank same-day & mail order weed delivery
                  </h2>

                  <div className="absolute bottom-0 right-0 w-full h-full flex items-end justify-end pointer-events-none">
                    <Image
                      width={320}
                      height={305}
                      className="w-full h-auto object-contain object-bottom-right"
                      alt="Delivery Airplane"
                      src="/media/pages/home/cta/delivery.webp"
                      loading="lazy"
                      decoding="async"
                      sizes="320px"
                    />
                  </div>

                  <div className="relative z-20 flex flex-col gap-2 self-start">
                    <Button asChild variant="bordered" color="white" size="lg" className="rounded-xl px-4 py-3 sm:w-max w-full">
                      <Link href="/search">Order delivery</Link>
                    </Button>
                  </div>
                </div>
              </div>

              <div className="relative aspect-[320/305] bg-blue-5 rounded-2xl overflow-hidden border-2 border-blue-100 transition-shadow">
                <div
                  className="absolute inset-0 bg-cover bg-center bg-blue-5"
                  style={{
                    backgroundImage: "url(/media/pages/home/cta/dank-club-background.webp)",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                ></div>

                <div className="relative z-10 flex flex-col justify-between h-full p-5">
                  <h2 className="text-body-18 xl:text-h4-desktop font-bold text-blue-100 leading-tight">
                    Join Dank Club and get 20% off your first order.
                  </h2>

                  <div className="absolute bottom-0 right-0 w-full h-full flex items-end justify-end pointer-events-none">
                    <Image
                      width={320}
                      height={305}
                      src="/media/pages/home/cta/dank-club.webp"
                      alt="Dank Club membership benefits - 20% off first order"
                      className="w-full h-auto object-contain object-bottom-right"
                      loading="lazy"
                      decoding="async"
                      sizes="320px"
                    />
                  </div>

                  <Button asChild variant="bordered" color="white" size="lg" className="rounded-xl px-4 py-3 sm:w-max w-full">
                    <Link href="/account?tab=club">Join Dank Club</Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:hidden relative px-4 pb-2 space-y-4">
            <div className="relative max-sm:hidden w-full aspect-[2/1] bg-constant-white rounded-2xl overflow-hidden shadow-lg">
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{
                  backgroundImage: "url(/media/pages/home/cta/pickup-background.webp)",
                }}
              ></div>
              <Image
                fill
                className="absolute inset-0 w-full h-full object-cover"
                alt="Cannabis Products"
                src="/media/pages/home/cta/pickup.webp"
                loading="lazy"
                decoding="async"
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
              <div className="relative z-10 flex flex-col justify-between h-full p-4">
                <h2 className="max-w-[70%] text-h4-mobile font-bold text-blue-100 leading-tight">
                  Pick up in store from one of the best menus in the game.
                </h2>
                <Button asChild variant="bordered" color="white" size="lg" className="rounded-xl px-4 py-3">
                  <Link href="/search?fulfillment=pickup">Pick up today</Link>
                </Button>
              </div>
            </div>

            <div className="overflow-x-auto scrollbar-hide -mx-2">
              <div className="flex gap-1 w-max">
                <div className="relative w-[282px] h-[242px] bg-blue-5 rounded-2xl overflow-hidden border-2 border-blue-100 flex-shrink-0">
                  <div
                    className="absolute inset-0"
                    style={{
                      backgroundImage: "url(/media/pages/home/cta/delivery-background.webp)",
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  ></div>

                  <div className="relative z-10 flex flex-col justify-between h-full p-4">
                    <h2 className="text-body-20 font-bold text-blue-100 leading-tight relative z-20">
                      Dank same-day & mail order weed delivery
                    </h2>

                    <div className="absolute inset-x-0 bottom-0 flex items-end justify-end pointer-events-none z-10">
                      <Image
                        width={282}
                        height={242}
                        className="w-full h-auto object-contain"
                        alt="Delivery"
                        src="/media/pages/home/cta/delivery.webp"
                        loading="lazy"
                        decoding="async"
                        sizes="(max-width: 640px) 100vw, 282px"
                      />
                    </div>

                    <div className="relative z-20 flex flex-col gap-1.5">
                      <Button asChild variant="bordered" color="white" size="sm" className="rounded-xl px-3 py-2.5">
                        <Link href="/search">Order delivery</Link>
                      </Button>
                      <Link
                        href="/how-delivery-works"
                        className="text-center text-body-12-desktop font-semibold text-blue-100 underline decoration-blue-100/40 underline-offset-2 transition-colors"
                      >
                        How it works
                      </Link>
                    </div>
                  </div>
                </div>

                <div className="relative w-[282px] h-[242px] bg-blue-5 rounded-2xl overflow-hidden border-2 border-blue-100 flex-shrink-0">
                  <div
                    className="absolute inset-0"
                    style={{
                      backgroundImage: "url(/media/pages/home/cta/dank-club-background.webp)",
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  ></div>

                  <div className="relative z-10 flex flex-col justify-between h-full p-4">
                    <h2 className="text-body-20 font-bold text-blue-100 leading-tight relative z-20">
                      Join Dank Club and get 20% off your first order.
                    </h2>

                    <div className="absolute inset-x-0 bottom-0 flex items-end justify-end pointer-events-none z-10">
                      <Image
                        width={282}
                        height={242}
                        src="/media/pages/home/cta/dank-club.webp"
                        alt="Dank Club membership package"
                        className="w-full h-auto object-contain"
                        loading="lazy"
                        decoding="async"
                        sizes="(max-width: 640px) 100vw, 282px"
                      />
                    </div>

                    <Button asChild variant="bordered" color="white" size="sm" className="rounded-xl px-3 py-2.5 relative z-20">
                      <Link href="/account?tab=club">Join Dank Club</Link>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

